import { AppNavLinks } from './AppNavLinks'
import styles from './AppSidebar.module.css'

type Props = {
  /** Icon-rail mode: narrows the sidebar and hides labels. Owned by
   * AppShell (which also drives the --sidebar-w layout variable). */
  collapsed: boolean
  onToggleCollapse: () => void
}

/**
 * Desktop-only vertical sidebar (visible at >= 1024 px viewports —
 * the visibility cutoff lives on the AppShell wrapper). Tucks below the
 * fixed top bar, which owns the brand + account; this rail keeps only the
 * collapse toggle at its head and the operational nav sections below
 * (docs/ux/2026-07-18-nav-top-plus-sidebar.md §2, Option A). When collapsed
 * it shrinks to a ~64 px icon rail (labels hidden, tooltips on hover) and the
 * main content + wide schedule reclaim the freed width via --sidebar-w.
 * See UX brief docs/ux/2026-05-16-workspace-luxury-redesign.md §4.3-§4.5.
 */
export function AppSidebar({ collapsed, onToggleCollapse }: Props) {
  return (
    <aside
      className={`${styles.sidebar} ${collapsed ? styles.collapsed : ''}`.trim()}
      aria-label="Primary"
    >
      <div className={styles.head}>
        <button
          type="button"
          className={styles.toggle}
          onClick={onToggleCollapse}
          aria-label={collapsed ? 'Expand menu' : 'Collapse menu'}
          aria-expanded={!collapsed}
          title={collapsed ? 'Expand menu' : 'Collapse menu'}
        >
          <ChevronIcon direction={collapsed ? 'right' : 'left'} />
        </button>
      </div>
      <AppNavLinks collapsed={collapsed} />
    </aside>
  )
}

function ChevronIcon({ direction }: { direction: 'left' | 'right' }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width="18"
      height="18"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {direction === 'left' ? <path d="m14 6-6 6 6 6" /> : <path d="m10 6 6 6-6 6" />}
    </svg>
  )
}
